import React from "react";
import { styled } from "@linaria/react";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import { useSetups } from "../../../../api/useSetups";
import t from "../../../../i18n";
import { color, lightest } from "@ui";
import Marker from "@ui/Highcharts/ui/Marker";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { fonts } from "@ui/Theme";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import { computeQuantityGap } from "diagnostics-domain/quantity/gap";
const Rect = styled.div<{ filled: boolean; color: string }>`
  height: 14px;
  width: 14px;
  border: 2px solid ${props => props.color};
  background-color: ${props => (props.filled ? props.color : lightest(props.color))};
`;

const RedFlag = styled.span`
  font: ${fonts.body2};
  color: ${color.oxygen400};
`;

interface P {
  analysis: ActiveAnalysis;
  project: Project;
}

function Legend({ analysis, project }: P) {
  const setups = useSetups(project.id);
  const setup = getAnalysisSetup(setups, analysis);
  const quantitiesValues = analysis.quantityValues;
  const activatedObservables =
    setup === null ? [] : setup.simulationOutputs.map(obs => obs.quantityId);

  const hasNoSymptom =
    setup === null
      ? true
      : Object.keys(quantitiesValues).some(quantityId => {
          const quantityValues = quantitiesValues[quantityId];
          const offset = getQuantityOffset(setup, quantityId);
          const gap = quantityValues
            ? computeQuantityGap(quantityValues.measured, quantityValues.simulated, offset)
            : null;

          return gap === null && activatedObservables.includes(quantityId);
        });

  return (
    <>
      <Marker
        name={t("diagnosis.charts.symptoms.legend.suspected")}
        description={t("diagnosis.charts.symptoms.legend.tooltip.suspected")}
      >
        <Rect color={color.raw.titanium600} filled={false} />
      </Marker>

      <Marker
        name={t("diagnosis.charts.symptoms.legend.confirmed")}
        description={t("diagnosis.charts.symptoms.legend.tooltip.confirmed")}
      >
        <Rect color={color.raw.titanium600} filled />
      </Marker>
      {hasNoSymptom && (
        <Marker
          name={t("diagnosis.charts.symptoms.no_symptoms")}
          description={t("diagnosis.charts.symptoms.legend.tooltip.no_symptom")}
        >
          <RedFlag>{"\u2716"}</RedFlag>
        </Marker>
      )}
      <Marker
        name={t("flags_legend.sigma")}
        description={t("diagnosis.charts.symptoms.legend.tooltip.sigma")}
      >
        <Rect color={color.chlorine100} filled />
      </Marker>
    </>
  );
}

export default Legend;
